import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { jwtDecode } from 'jwt-decode';
import ls from 'localstorage-slim';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { generateTimestamp } from 'src/app/shared/utils/date.utils';
import { $encrypt, sign } from 'src/app/shared/utils/signing.utils';
import { AppState } from 'src/app/store';
import { environment } from '../../../../environments/environment';
import {
  AUTHORIZATION,
  AUTO_LOGOUT,
  PRIVATE_KEY,
  X_CLIENT_ID,
  X_CLIENT_TYPE,
  X_FORWARDED_FOR,
  X_PLATFORM_IP
} from '../../../app.constants';
import { reduxReset } from '../../../store/actions/root/root.actions';
import { Authentication } from '../models/authentication.model';
import { OTPCredentials } from '../models/otp-credentials.model';
import { PlatformType } from './../../../enums/platform-type.enum';
ls.config.encrypt = true;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private tokenSubject = new BehaviorSubject<string | null>(null);
  httpReqError = new BehaviorSubject<string>('');
  decodedToken: { [key: string]: string };
  loginSign = {};

  constructor(
    private http: HttpClient,
    private router: Router,
    private store: Store<AppState>
  ) {
    this.decodedToken = {};
  }
  getPublicKeyB64(): string {
    if (environment.production === false) {
      const storedPrivateKey = ls.get(PRIVATE_KEY);
      if (!storedPrivateKey) {
        const newPrivateKey = $encrypt.getPrivateKeyB64();
        ls.set(PRIVATE_KEY, newPrivateKey);
        $encrypt.setPrivateKey(newPrivateKey);
      } else {
        $encrypt.setPrivateKey(storedPrivateKey);
      }
    }
    return $encrypt.getPublicKeyB64();
  }

  login(
    credentials: OTPCredentials,
    currentLang?: string
  ): Observable<HttpResponse<Authentication>> {
    const timestamp = generateTimestamp();

    const loginSign = {
      userCode: credentials.userCode,
      otp: credentials.otp,
      platformType: PlatformType.WEB,
      publicKey: credentials.publicKey,
      timestamp: timestamp,
      secret: environment.SECRET_KEY
    };
    const signature = Object.values(loginSign).join('|');

    return this.getIPAddress().pipe(
      switchMap(ipAddress => {
        ls.set('X_PLATFORM_IP', ipAddress);
        const headers = new HttpHeaders()
          .set('X-Timestamp', timestamp)
          .set('X-Platform-Type', PlatformType.WEB)
          .set('X-Signature', sign(signature))
          .set('X-Platform-IP', ipAddress)
          .set('Accept-Language', currentLang || 'en');

        return this.http.post<Authentication>(
          `${environment.baseUrl}/auth/otp-login`,
          credentials,
          { headers, observe: 'response' }
        );
      })
    );
  }

  getIPAddress(): Observable<string> {
    return this.http
      .get<{ ip: string }>('https://api.ipify.org?format=json')
      .pipe(
        map(response => response.ip),

        catchError(error => {
          console.error('Error fetching IP address:', error);
          throw new Error('IP address retrieval failed.');
        })
      );
  }

  // Add a method to expose the IP address subject
  getIpAddressSubject(): any {
    const ipAddressSubject =
      ls.get(X_FORWARDED_FOR) || ls.get(X_PLATFORM_IP) || '';
    return ipAddressSubject ? ipAddressSubject : '';
  }

  getToken(): string {
    const token = ls.get(AUTHORIZATION);
    return typeof token === 'string' ? token : '';
  }

  setToken(token: string): void {
    if (token) {
      ls.set(AUTHORIZATION, token);
      ls.set('token_timestamp', Date.now().toString());
      this.tokenSubject.next(token);
    }
  }

  clearToken(): void {
    ls.remove(AUTHORIZATION);
    ls.remove('token_timestamp');
    this.tokenSubject.next(null);
  }

  private isTokenExpired(): boolean {
    const timestamp = ls.get('token_timestamp');
    if (!timestamp) return true;
    
    const tokenAge = Date.now() - parseInt(timestamp as string);
    const isExpired = tokenAge > 5 * 60 * 1000; // 5 minutes in milliseconds
    if (isExpired) {
      this.clearToken();
    }
    return isExpired;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    
    // During login, don't check expiration
    if (this.router.url === '/login') {
      return true;
    }
    
    return !this.isTokenExpired();
  }

  decodeToken() {
    if (this.getToken()) {
      this.decodedToken = jwtDecode(this.getToken() as string);
    }
  }

  getDecodeToken() {
    const token = this.getToken();
    if (token) {
      return jwtDecode(token);
    }
    return null;
  }
  getClientId(): string {
    return ls.get(X_CLIENT_ID) || '';
  }

  getClientType(): string {
    return ls.get(X_CLIENT_TYPE) || '';
  }

  isProfileSelected(): boolean {
    const clientId = this.getClientId();
    return !!clientId;
  }

  getAutoLogout(): boolean {
    const value = ls.get(AUTO_LOGOUT);
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }
    return value === true;
  }

  setAutoLogout(autoLogout: boolean): void {
    ls.set(AUTO_LOGOUT, Boolean(autoLogout));
  }

  logout(): void {
    let lang = ls.get('language');
    ls.clear();
    if (lang) ls.set('language', lang);
    this.tokenSubject.next(null);
    this.store.dispatch(reduxReset());
    this.router.navigateByUrl('/login');
  }

  logoutApi(): Observable<any> {
    return this.http.post(`${environment.baseUrl}/auth/logout`, null).pipe(
      map(response => {
        return response;
      })
    );
  }

  ngOnDestroy(): void {
    this.tokenSubject.next(null);
  }
}
